import './AnchorableDrawer.scss';

import { PushpinOutlined } from '@ant-design/icons';
import { Button, Divider, Drawer, Flex } from 'antd';
import { DrawerProps } from 'antd/lib';
import { CSSProperties, useEffect, useState } from 'react';

import { useUiContext } from '@/contexts/UiContext';

interface AnchorableDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  anchorableDrawer: boolean;
  setAnchorableDrawer: (anchorable: boolean) => void;
  title: React.ReactNode;
  className: string;
  classNames: DrawerProps['classNames'];
  width?: number | string;
  children: React.ReactNode;
}

/**
 * Drawer que puede anclarse en la pantalla.
 * El desplazamiento del contenido al anclar ya está resuelto globalmente para
 * cualquier contenedor con la clase .MainSection (ver .MainSection:has(.AnchorableDrawer--anchored)
 * en src/index.scss), que envuelve todas las páginas de sección. No hace falta
 * agregar una clase extra al usar este componente dentro de una de esas páginas.
 */
const AnchorableDrawer = (props: AnchorableDrawerProps) => {
  const {
    open,
    setOpen,
    anchorableDrawer,
    setAnchorableDrawer,
    title,
    className,
    classNames,
    width,
    children,
  } = props;
  const { isMediumScreen } = useUiContext();
  const [scrollOffset, setScrollOffset] = useState(0);
  const drawerRootStyle: CSSProperties = anchorableDrawer
    ? { position: 'fixed', top: 64, right: 0, height: 'calc(100dvh - 64px)', zIndex: 100 }
    : {
        position: 'absolute',
        top: scrollOffset,
        right: 0,
        height: 'calc(100dvh - 64px)',
        zIndex: 10,
      };

  useEffect(() => {
    if (!open || anchorableDrawer) {
      return;
    }

    const mainElement = document.querySelector('main.ant-layout-content');

    setScrollOffset(mainElement?.scrollTop ?? 0);
  }, [open, anchorableDrawer]);

  useEffect(() => {
    if (anchorableDrawer && isMediumScreen) {
      setAnchorableDrawer(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMediumScreen]);

  return (
    <div
      className={`AnchorableDrawer ${open ? (anchorableDrawer ? 'AnchorableDrawer--anchored' : 'AnchorableDrawer--not-anchored') : ''}`}>
      <Drawer
        classNames={classNames}
        className={className}
        title={
          <Flex align="center">
            {!isMediumScreen && (
              <>
                <Button
                  size="small"
                  type={anchorableDrawer ? 'primary' : 'default'}
                  onClick={() => setAnchorableDrawer(!anchorableDrawer)}
                  icon={<PushpinOutlined />}
                />
                <Divider type="vertical" />
              </>
            )}
            {title}
          </Flex>
        }
        placement="right"
        width={width}
        mask={anchorableDrawer ? false : true}
        closable={false}
        onClose={() => setOpen(false)}
        open={open}
        getContainer={() => document.querySelector('main.ant-layout-content')!}
        rootStyle={drawerRootStyle}>
        {children}
      </Drawer>
    </div>
  );
};

export default AnchorableDrawer;
